import { TableLocal, TableLocalPagination } from "components/Common/TableLocal"
import { useCallback, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Badge } from "reactstrap"
import ButtonTooltip from "components/Common/ButtonTooltip"
import ConfirmModal from "components/Common/ConfirmModal"
import DeleteModal from "components/Common/DeleteModal"
import { formatReadableDate } from "utils/formatDate"
import { formatBDT } from "utils/formatPrice"
import { downloadAndOpenFile } from "api/pdfApi"
import {
  useCancelPreOrder,
  useCreateMoneyReceiptFromPreOrder,
  useDeletePreOrder,
} from "hooks/usePreOrder"
import { useToastMessage } from "hooks/useToastMessage"
import { useListParams } from "hooks/useListParams"
import GenerateMoneyReceiptModal from "./GenerateMoneyReceiptModal"

const PreOrderList = ({ data, customer, onChanged }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { handleResponse, handleError } = useToastMessage()
  const { params, updateParams } = useListParams({
    pb_page: 1,
    pb_per_page: 5,
  })
  const currentPage = Number(params?.pb_page) || 1
  const itemsPerPage = Number(params?.pb_per_page) || 5

  const [receiptModal, setReceiptModal] = useState(null)
  const [cancelModal, setCancelModal] = useState(null)
  const [deleteModal, setDeleteModal] = useState(null)
  const [downloadingId, setDownloadingId] = useState(null)

  const { mutateAsync: deletePreOrder, isPending: isDeleting } =
    useDeletePreOrder()
  const { mutateAsync: cancelPreOrder, isPending: isCancelling } =
    useCancelPreOrder()
  const { mutateAsync: createMoneyReceipt, isPending: creatingReceipt } =
    useCreateMoneyReceiptFromPreOrder()

  const sortedContents = useMemo(() => {
    const contents = Array.isArray(data) ? data : []
    return [...contents].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    )
  }, [data])

  const handlePageChange = useCallback(
    page => updateParams({ pb_page: page }),
    [updateParams]
  )
  const handleItemsPerPageChange = useCallback(
    n => updateParams({ pb_per_page: n, pb_page: 1 }),
    [updateParams]
  )

  const handleDownload = async item => {
    setDownloadingId(item.id)
    try {
      await downloadAndOpenFile({ url: `/pre-orders/${item.id}/pdf` })
    } catch (error) {
      handleError(error, "Failed to download pre-order PDF.")
    } finally {
      setDownloadingId(null)
    }
  }

  const handleDelete = async item => {
    try {
      const response = await deletePreOrder({ id: item.id })
      handleResponse(response, "Pre-order deleted successfully.", true)
      setDeleteModal(null)
      onChanged?.()
    } catch (error) {
      handleError(error, "Failed to delete pre-order.")
    }
  }

  const handleCancel = async () => {
    const item = cancelModal
    setCancelModal(null)
    try {
      const response = await cancelPreOrder(item.id)
      handleResponse(response, "Pre-order cancelled successfully.", true)
      onChanged?.()
    } catch (error) {
      handleError(error, "Failed to cancel pre-order.")
    }
  }

  const handleGenerateReceipt = async payload => {
    try {
      const response = await createMoneyReceipt(payload)
      handleResponse(response, "Money receipt generated successfully.", true)
      onChanged?.()
      const moneyReceiptId = response?.data?.id
      if (moneyReceiptId) {
        navigate(`/money-receipts/${moneyReceiptId}`)
      }
    } catch (error) {
      handleError(error, "Failed to generate money receipt.")
      throw error
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "Booking",
        cell: item => (
          <div>
            <div className="fw-bold text-primary mb-1">{item?.poid ?? "-"}</div>
            {item?.vehicle ? (
              <>
                <div className="small text-muted">
                  Stock: {item?.vehicle?.stock_id ?? "-"}
                </div>
                <div className="small text-muted">
                  Chassis: {item?.vehicle?.full_chassis ?? "-"}
                </div>
                <div className="small text-muted">
                  Engine: {item?.vehicle?.full_engine ?? "-"}
                </div>
              </>
            ) : (
              <div className="small text-muted">No vehicle matched yet</div>
            )}
            {item?.vehicle_description ? (
              <div className="small">{item.vehicle_description}</div>
            ) : null}
          </div>
        ),
      },
      {
        header: "Deposit",
        cell: item => (
          <div className="fw-bold text-success">
            {formatBDT(item?.deposit_amount)}
          </div>
        ),
      },
      {
        header: "Status",
        cell: item => (
          <Badge color={item?.status === "cancelled" ? "danger" : "success"}>
            {item?.status === "cancelled" ? "Cancelled" : "Booked"}
          </Badge>
        ),
      },
      {
        header: "Date",
        cell: item => (
          <div>{formatReadableDate(item?.created_at, "MMM d, y")}</div>
        ),
      },
      {
        header: "Action",
        cell: item => (
          <ul className="list-unstyled d-flex flex-wrap gap-1 mb-0">
            <li>
              <ButtonTooltip
                type="view"
                btnId={`pre-order-view-${item.id}`}
                onClick={() =>
                  navigate(`/pre-orders/${item.id}`, {
                    state: { from: location },
                  })
                }
              />
            </li>
            <li>
              <ButtonTooltip
                type="edit"
                btnId={`pre-order-edit-${item.id}`}
                onClick={() =>
                  navigate(`/pre-orders/${item.id}/edit`, {
                    state: { from: location },
                  })
                }
              />
            </li>
            <li>
              <ButtonTooltip
                type="pdf"
                btnId={`pre-order-pdf-${item.id}`}
                loading={downloadingId === item.id}
                onClick={() => handleDownload(item)}
              />
            </li>
            <li>
              <ButtonTooltip
                type="receipt"
                btnId={`pre-order-mr-${item.id}`}
                btnName={
                  item?.vehicle_id
                    ? "Generate Money Receipt"
                    : "Add chassis/engine number first"
                }
                loading={creatingReceipt}
                disabled={!item?.vehicle_id || item?.status === "cancelled"}
                onClick={() => setReceiptModal(item)}
              />
            </li>
            {item?.status !== "cancelled" ? (
              <li>
                <ButtonTooltip
                  type="cancel"
                  btnId={`pre-order-cancel-${item.id}`}
                  loading={isCancelling}
                  onClick={() => setCancelModal(item)}
                />
              </li>
            ) : null}
            <li>
              <ButtonTooltip
                type="delete"
                btnId={`pre-order-delete-${item.id}`}
                loading={isDeleting}
                onClick={() => setDeleteModal(item)}
              />
            </li>
          </ul>
        ),
      },
    ],
    [creatingReceipt, isCancelling, isDeleting, downloadingId, navigate, location]
  )

  return (
    <>
      <TableLocal
        columns={columns}
        data={sortedContents}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        tableClass="align-middle table-hover mb-0"
        getRowKey={row => row.id}
        emptyMessage={
          <div className="text-center py-5">
            <i className="bx bx-calendar-check bx-3x text-muted mb-3"></i>
            <h5 className="text-muted">No pre-orders found</h5>
            <p className="text-muted mb-0">
              Create the first pre-order to get started
            </p>
          </div>
        }
      />

      <TableLocalPagination
        data={sortedContents}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        showItemsPerPage={true}
        itemsPerPageOptions={[5, 10, 20]}
        className="px-3 py-3 border-top"
      />

      {receiptModal ? (
        <GenerateMoneyReceiptModal
          isOpen={!!receiptModal}
          toggle={() => setReceiptModal(null)}
          preOrder={receiptModal}
          onSubmit={handleGenerateReceipt}
          isSubmitting={creatingReceipt}
        />
      ) : null}

      <ConfirmModal
        isOpen={!!cancelModal}
        toggle={() => setCancelModal(null)}
        title="Cancel Pre-Order?"
        message="This will cancel the pre-order and release the reserved vehicle if no other active booking holds it. Do you want to proceed?"
        confirmLabel="Cancel Booking"
        icon="mdi-close-circle-outline"
        onConfirm={handleCancel}
        isConfirming={isCancelling}
      />

      <DeleteModal
        show={!!deleteModal}
        onDeleteClick={() => handleDelete(deleteModal)}
        onCloseClick={() => setDeleteModal(null)}
        loading={isDeleting}
      />
    </>
  )
}

export default PreOrderList
